import {useState, useCallback, useMemo} from 'react';
import {debounce} from "lodash";
import {ViewportProps} from "../../../../types/map";
import {getBounds} from "../MapHelper";
import {normalizeLongitude} from "../../../../utils/normalizedLongitude";

interface MapBoundsZoom {
  bounds: [number, number, number, number];
  zoom: number;
}

interface UseMapViewportProps {
  initialViewState: ViewportProps;
  setMapBoundsZoom: (boundsZoom: MapBoundsZoom) => void;
  updatedBounds?: boolean
}

interface UseMapViewportReturn {
  viewState: ViewportProps;
  setViewState: React.Dispatch<React.SetStateAction<ViewportProps>>;
  loaded: boolean;
  updateMapBounds: (vp: ViewportProps, update?: boolean) => void;
  handleViewportChange: (evt: { viewState: ViewportProps }) => void;
  handleOnLoad: () => void;
  handleOnResize: (evt: any) => void;
  handleZoomPan: (evt: any) => void;
}

export function useMapViewport(
  { initialViewState, setMapBoundsZoom, updatedBounds }: UseMapViewportProps
): UseMapViewportReturn {
  const [viewState, setViewState] = useState<ViewportProps>(initialViewState);
  const [loaded, setLoaded] = useState<boolean>(false);

  const updateMapBounds = useCallback((vp: ViewportProps, update = false): void => {
    const bounds = getBounds(vp);
    const nw = bounds[0];
    const se = bounds[1];
    const longBegin = nw[0];
    const longEnd = se[0];
    const latBegin = nw[1];
    const latEnd = se[1];
    if (update || updatedBounds) {
      setMapBoundsZoom({
        bounds: [longBegin, latBegin, longEnd, latEnd],
        zoom: vp.zoom,
      });
    }
  }, [setMapBoundsZoom, updatedBounds]);

  const debouncedUpdateMapBounds = useMemo(() =>
      debounce(updateMapBounds, 300),
    [updateMapBounds]
  );

  const handleViewportChange = useCallback((evt: { viewState: ViewportProps }): void => {
    const normalizedViewState = {
      ...evt.viewState,
      longitude: normalizeLongitude(evt.viewState.longitude)
    };

    setViewState(normalizedViewState);
  }, []);

  const handleOnLoad = useCallback((): void => {
    updateMapBounds(viewState, true);
    setLoaded(true);
  }, [viewState, updateMapBounds]);

  const handleOnResize = useCallback((evt: any): void => {
    const vp = {
      ...viewState,
      width: evt.target._containerWidth,
      height: evt.target._containerHeight
    };
    debouncedUpdateMapBounds(vp)
  }, [viewState, updateMapBounds]);

  const handleZoomPan = useCallback((evt: any): void => {
    const newViewState = evt.viewState
    const normalizedLongitude = normalizeLongitude(newViewState.longitude)
    const vp = {
      ...newViewState,
      longitude: normalizedLongitude,
      width: evt.target._containerWidth,
      height: evt.target._containerHeight
    }
    debouncedUpdateMapBounds(vp)
  }, [debouncedUpdateMapBounds])

  return {
    viewState,
    setViewState,
    loaded,
    updateMapBounds,
    handleViewportChange,
    handleOnLoad,
    handleOnResize,
    handleZoomPan
  };
}
